import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { FilterSelectorModal } from '../modalDialog/FilterSelectorModal'
import { RawDataModal } from '../modalDialog/RawDataModal'
import { ChartFilter, CurrentChartFilter } from '../types'
import { Button } from '@/Atomic/Molecules/Button'
import './ChartHeader.scss'

interface Param {
  title: string | null | undefined
  subTitle?: string | undefined
  rawData?: string[] | undefined
  filters?: ChartFilter[] | undefined
  onFilterChange?: (filter: CurrentChartFilter[]) => void
  hasExpand?: boolean | undefined
}

export function ChartHeader({
  title,
  subTitle,
  rawData = [],
  filters = [],
  onFilterChange = () => {},
  hasExpand = true,
}: Param) {
  const { t } = useTranslation()
  const [isDataModalOpen, setDataModalIsOpen] = useState(false)
  const openDataModal = () => setDataModalIsOpen(true)
  const closeDataModal = () => setDataModalIsOpen(false)

  const [isFilterOpen, setFilterIsOpen] = useState(false)
  const openFilter = () => setFilterIsOpen(true)
  const closeFilter = () => setFilterIsOpen(false)

  const [isExpanded, setIsExpanded] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  useEffect(() => {
    const wrapper = headerRef.current?.closest('.chartWrapper')
    if (!wrapper) return
    wrapper.classList.toggle('chartExpanded', isExpanded)

    // Trigger window resize event to force chart re-render after DOM updates
    if (isExpanded) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 50)
    }

    return () => {
      wrapper.classList.remove('chartExpanded')
    }
  }, [isExpanded])

  useEffect(() => {
    if (!isExpanded) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExpanded(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isExpanded])

  const hasFilter = filters && filters.length > 0

  const nbsp = '\u00A0'
  const hasSubTitle = !!subTitle

  const hasRawData = hasSubTitle && rawData.length > 0

  if (rawData.length > 1 && rawData[rawData.length - 2] !== '') {
    rawData.splice(rawData.length - 1, 0, '')
  }

  return (
    <div
      ref={headerRef}
      className={'chartHeader' + (hasFilter ? ' hasFilter' : '')}
    >
      <div className="chartTitleWrap">
        <h6 className="mb-0 chartTitle">{title}</h6>
        <span
          className={
            'text-small text-meta chartSubtitle' +
            (hasRawData ? ' hasLink' : '')
          }
          onClick={hasRawData ? openDataModal : undefined}
        >
          {subTitle || nbsp}
        </span>
      </div>
      <div className="chartToolbox">
        {hasFilter && (
          <Button
            aria-label={t('common.filter.title')}
            tooltip={t('common.filter.title')}
            variant="icon"
            icon="filter"
            onClick={openFilter}
          />
        )}
        {hasExpand &&
          (isExpanded ? (
            <Button
              aria-label={t('common.close')}
              tooltip={t('common.close')}
              variant="icon"
              icon="cross"
              onClick={toggleExpanded}
            />
          ) : (
            <Button
              aria-label={t('common.expand')}
              tooltip={t('common.expand')}
              variant="icon"
              icon="expand"
              onClick={toggleExpanded}
            />
          ))}
      </div>
      {hasRawData && (
        <RawDataModal
          isOpen={isDataModalOpen}
          close={closeDataModal}
          title={title || ''}
          data={rawData}
        />
      )}
      {hasFilter && (
        <FilterSelectorModal
          isOpen={isFilterOpen}
          close={closeFilter}
          title={t('common.filter.title')}
          filters={filters}
          onFilterChange={onFilterChange}
          confirmationButtonText={t('common.ok')}
        />
      )}
    </div>
  )
}
